import { ExternalLink, MoreVertical } from "lucide-react";
import { useState } from "react";
import { Skeleton } from "../ui/skeleton";
import { Spinner } from "@/components/Spinner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export interface Document {
  id: string;
  name: string;
  url: string | null;
  type?: {
    id: string;
    label: string;
  } | null;
}

interface DocumentCardProps {
  document: Document;
  onDelete?: (documentId: string) => Promise<void>;
}

export function DocumentCard({ document, onDelete }: DocumentCardProps) {
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (!onDelete) return;
    setIsDeleting(true);
    try {
      await onDelete(document.id);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Card className="p-4">
      <div className="flex justify-between items-start">
        <CardTitle className="font-normal" data-testid="document-title">
          {document.name}
        </CardTitle>
        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="sm"
            className={"h-8 w-8 p-0 disabled:pointer-events-auto disabled:cursor-not-allowed"}
            title={document.url ? "Open document" : "Document unavailable"}
            disabled={!document.url}
            onClick={() => {
              if (document.url) {
                window.open(document.url, "_blank", "noopener,noreferrer");
              }
            }}
          >
            <ExternalLink className="h-4 w-4" />
            <span className="sr-only">
              {document.url ? `Open ${document.name} in new tab` : `${document.name} is unavailable`}
            </span>
          </Button>
          {onDelete ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" disabled={isDeleting} size="sm" className="h-8 w-8 p-0" title="More options">
                  {isDeleting ? <Spinner className="h-4 w-4" /> : <MoreVertical className="h-4 w-4" />}
                  <span className="sr-only">More options for {document.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem className="text-destructive focus:text-destructive/80" onClick={handleDelete}>
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>
      </div>
      {document.type?.label && (
        <Badge variant="outline" className="rounded-full p-2">
          {document.type.label}
        </Badge>
      )}
    </Card>
  );
}

export function DocumentCardSkeleton() {
  return (
    <Card className="p-4" data-testid="document-card-skeleton">
      <div className="flex justify-between items-start">
        <Skeleton className="h-6 w-48" />
        <div className="flex items-center space-x-2">
          <Skeleton className="h-8 w-8 rounded-md" />
          <Skeleton className="h-8 w-8 rounded-md" />
        </div>
      </div>
      <Skeleton className="h-6 w-24 mt-2 rounded-full" />
    </Card>
  );
}
